import {
  parseDate,
  today,
  getLocalTimeZone,
  type CalendarDate,
  type DateValue,
} from '@internationalized/date';

/**
 * Shared cross-widget storage key used to persist the search selection
 * (dates + guests). Other Eliophot widgets can read the same key to
 * pre-fill themselves with the user's last choices.
 */
export const SEARCH_SELECTION_STORAGE_KEY = 'qs.selection';

/**
 * Stores the current redirect/booking URL so other widgets or scripts
 * on the same page can read it without recomputing the query params.
 */
export const REDIRECT_URL_STORAGE_KEY = 'qs.url';

/**
 * Same-tab notification bus for selection changes.
 * `storage` events only fire across tabs, so we mirror updates onto a
 * window-level CustomEvent so widgets on the same page (e.g. mini room
 * price widgets) can refetch when the main QuickSearch widget updates
 * the shared selection.
 */
export const SELECTION_CHANGE_EVENT = 'qs:selection-change';

/** Bump this if the stored shape changes incompatibly. */
const STORAGE_VERSION = 1;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export type SearchSelection = {
  /** ISO YYYY-MM-DD */
  checkin: string;
  /** ISO YYYY-MM-DD */
  checkout: string;
  guests: number;
};

type StoredSelection = SearchSelection & {
  version: number;
  updatedAt: number;
};

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadSelection(): SearchSelection | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(SEARCH_SELECTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSelection> | null;
    if (!parsed || parsed.version !== STORAGE_VERSION) return null;
    if (
      typeof parsed.checkin !== 'string' ||
      typeof parsed.checkout !== 'string' ||
      typeof parsed.guests !== 'number'
    ) {
      return null;
    }
    if (!ISO_DATE_RE.test(parsed.checkin) || !ISO_DATE_RE.test(parsed.checkout)) {
      return null;
    }
    return {
      checkin: parsed.checkin,
      checkout: parsed.checkout,
      guests: Math.max(1, Math.floor(parsed.guests)),
    };
  } catch {
    return null;
  }
}

export function saveSelection(selection: SearchSelection): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const data: StoredSelection = {
      ...selection,
      version: STORAGE_VERSION,
      updatedAt: Date.now(),
    };
    storage.setItem(SEARCH_SELECTION_STORAGE_KEY, JSON.stringify(data));
    notifySelectionChange(selection);
  } catch {
    /* quota exceeded / private mode — silently ignore */
  }
}

export function clearSelection(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(SEARCH_SELECTION_STORAGE_KEY);
    notifySelectionChange(null);
  } catch {
    /* ignore */
  }
}

export function saveRedirectUrl(url: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(REDIRECT_URL_STORAGE_KEY, url);
  } catch {
    /* quota exceeded / private mode — silently ignore */
  }
}

function notifySelectionChange(selection: SearchSelection | null): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<SearchSelection | null>(SELECTION_CHANGE_EVENT, {
        detail: selection,
      }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Subscribe to selection changes. Fires for both same-tab updates
 * (custom event dispatched by `saveSelection`) and cross-tab updates
 * (native `storage` event on `qs.selection`). Returns an unsubscribe.
 */
export function onSelectionChange(
  cb: (selection: SearchSelection | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handleCustom = (e: Event) => {
    const detail = (e as CustomEvent<SearchSelection | null>).detail ?? null;
    cb(detail);
  };

  const handleStorage = (e: StorageEvent) => {
    if (e.key !== SEARCH_SELECTION_STORAGE_KEY) return;
    cb(loadSelection());
  };

  window.addEventListener(SELECTION_CHANGE_EVENT, handleCustom);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(SELECTION_CHANGE_EVENT, handleCustom);
    window.removeEventListener('storage', handleStorage);
  };
}

function formatISO(d: CalendarDate): string {
  const y = String(d.year).padStart(4, '0');
  const m = String(d.month).padStart(2, '0');
  const day = String(d.day).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Ensure `qs.selection` exists in localStorage. If no selection is stored
 * yet, persist sensible defaults (tomorrow → +1 night, 2 guests) so that
 * external scripts can read the value without waiting for user interaction.
 */
export function seedSelectionDefaults(): SearchSelection {
  const existing = loadSelection();
  if (existing) return existing;

  const tz = getLocalTimeZone();
  const checkin = today(tz).add({ days: 1 });
  const checkout = today(tz).add({ days: 2 });

  const defaults: SearchSelection = {
    checkin: formatISO(checkin),
    checkout: formatISO(checkout),
    guests: 2,
  };

  saveSelection(defaults);
  return defaults;
}

export function parseISODate(iso: string): CalendarDate | null {
  if (!ISO_DATE_RE.test(iso)) return null;
  try {
    return parseDate(iso);
  } catch {
    return null;
  }
}

export function isFutureOrToday(date: DateValue): boolean {
  return date.compare(today(getLocalTimeZone())) >= 0;
}
